import React, { useEffect, useMemo, useRef, useState } from 'react';
import './DragGame.css';
import { useSocket } from '../context/SocketContext';
import ProfileBadge from '../components/ProfileBadge';
import ProfileImage from '../components/ProfileImage';
import BrandFooter from '../components/BrandFooter';
import PawMark from '../components/PawMark';
import ScoreBox from '../components/ScoreBox';

// Joystick-handle paw (pink). Download from Figma "Cursur" -> public/assets/paw.png
const PAW_SRC = `${process.env.PUBLIC_URL}/assets/paw.png`;

/**
 * CONTENT1 — joystick positioning game.
 * Drag the joystick: the stick displacement (deadzone-normalized, -1..1)
 * is emitted as `icon_speed` at 30fps. TouchDesigner integrates it into a
 * position and echoes it back as `icon` (x,y in -1..1), which moves the dot.
 * Ported from mobile-sensors/frontend/src/pages/Joystick.js.
 *
 * The OX-quiz score (TouchDesigner's `val/score` OSC) is shown in the
 * ScoreBox below — the same live score the tap screen displays.
 */

const DEAD_ZONE = 0.06;
const STICK_SPRING = 60;
const STICK_DAMP = 18;
const EMIT_FPS = 30;
const HANDLE_SIZE = 52; // must match .drag-joystick-handle width in CSS

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const len = (x, y) => Math.hypot(x, y);

function normalizeDeadzone(x, y) {
  const d = len(x, y);
  if (d <= DEAD_ZONE) return { x: 0, y: 0 };
  const m = (d - DEAD_ZONE) / (1 - DEAD_ZONE);
  return { x: (x / d) * m, y: (y / d) * m };
}

function softClamp(x, y) {
  const d = len(x, y);
  if (d <= 1) return { x, y };
  return { x: x / d, y: y / d };
}

export default function DragGame() {
  const { userInfo, sendIconSpeed, socket, score } = useSocket();

  const mapRef = useRef(null);
  const baseRef = useRef(null);

  const [stickPos, setStickPos] = useState({ x: 0, y: 0 });
  const [dotPos, setDotPos] = useState({ x: -100, y: -100 });

  const sizes = useRef({ mapW: 0, mapH: 0, dotR: 16, stickMaxR: 56 });
  const draggingRef = useRef(false);
  const pointerIdRef = useRef(null);
  const targetRef = useRef({ x: 0, y: 0 });
  const stickRef = useRef({ x: 0, y: 0, vx: 0, vy: 0 });
  const lastEmitRef = useRef(0);

  // ---- measure the map + joystick ----
  useEffect(() => {
    const mapEl = mapRef.current;
    const baseEl = baseRef.current;
    if (!mapEl || !baseEl) return;

    const measure = () => {
      const m = mapEl.getBoundingClientRect();
      const b = baseEl.getBoundingClientRect();
      sizes.current.mapW = m.width;
      sizes.current.mapH = m.height;
      sizes.current.stickMaxR = Math.max(1, (b.width - HANDLE_SIZE) / 2);

      setDotPos((p) => {
        if (p.x >= 0) return p;
        return { x: m.width / 2, y: m.height / 2 };
      });
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(mapEl);
    ro.observe(baseEl);
    return () => ro.disconnect();
  }, []);

  // ---- TD position echo -> dot ----
  useEffect(() => {
    if (!socket) return;
    const onIcon = (p) => {
      const xN = Number(p?.x);
      const yN = Number(p?.y);
      if (!Number.isFinite(xN) || !Number.isFinite(yN)) return;
      const { mapW, mapH, dotR } = sizes.current;
      if (mapW <= 0 || mapH <= 0) return;
      const cx = mapW / 2;
      const cy = mapH / 2;
      const hx = Math.max(1, cx - dotR);
      const hy = Math.max(1, cy - dotR);
      setDotPos({
        x: clamp(cx + xN * hx, dotR, mapW - dotR),
        y: clamp(cy + yN * hy, dotR, mapH - dotR),
      });
    };
    socket.on('icon', onIcon);
    return () => socket.off('icon', onIcon);
  }, [socket]);

  // ---- joystick pointer handling ----
  const updateTargetFromPointer = (e) => {
    const base = baseRef.current;
    if (!base) return;
    const r = base.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const maxR = sizes.current.stickMaxR || (r.width - HANDLE_SIZE) / 2;
    targetRef.current = softClamp((e.clientX - cx) / maxR, (e.clientY - cy) / maxR);
  };

  const onPointerDown = (e) => {
    const base = baseRef.current;
    if (!base) return;
    draggingRef.current = true;
    pointerIdRef.current = e.pointerId;
    base.setPointerCapture?.(e.pointerId);
    updateTargetFromPointer(e);
  };

  const onPointerMove = (e) => {
    if (!draggingRef.current || pointerIdRef.current !== e.pointerId) return;
    updateTargetFromPointer(e);
  };

  const onPointerUp = (e) => {
    if (pointerIdRef.current !== e.pointerId) return;
    draggingRef.current = false;
    pointerIdRef.current = null;
    targetRef.current = { x: 0, y: 0 }; // spring back to center
  };

  // ---- animation loop: spring the stick + emit at 30fps ----
  useEffect(() => {
    let raf = 0;
    let lastT = performance.now();

    const tick = (t) => {
      const dt = Math.min(0.033, (t - lastT) / 1000);
      lastT = t;

      const target = targetRef.current;
      const s = stickRef.current;
      if (draggingRef.current) {
        s.x = target.x; s.y = target.y; s.vx = 0; s.vy = 0;
      } else {
        const ax = STICK_SPRING * (target.x - s.x) - STICK_DAMP * s.vx;
        const ay = STICK_SPRING * (target.y - s.y) - STICK_DAMP * s.vy;
        s.vx += ax * dt; s.vy += ay * dt;
        s.x += s.vx * dt; s.y += s.vy * dt;
      }

      const maxR = sizes.current.stickMaxR || 56;
      setStickPos({ x: s.x * maxR, y: s.y * maxR });

      const now = performance.now();
      if (now - lastEmitRef.current > 1000 / EMIT_FPS) {
        lastEmitRef.current = now;
        const dz = normalizeDeadzone(s.x, s.y);
        sendIconSpeed(dz.x, -dz.y); // invert Y so dragging up moves up
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dotStyle = useMemo(() => ({
    transform: `translate(${dotPos.x}px, ${dotPos.y}px) translate(-50%, -50%)`,
  }), [dotPos.x, dotPos.y]);

  return (
    <div className="drag-screen">
      <div className="drag-inner">
        <div className="drag-badge">
          <ProfileBadge
            index={userInfo.profileIndex}
            src={userInfo.profileImage}
            nickname={userInfo.nickname}
          />
        </div>

        {/* positioning field */}
        <div ref={mapRef} className="drag-map">
          <ProfileImage
            index={userInfo.profileIndex}
            src={userInfo.profileImage}
            className="drag-dot"
            style={dotStyle}
            alt="내 위치"
          />
        </div>

        {/* joystick — caved-in base, raised handle */}
        <div className="drag-joystick-area">
          <div
            ref={baseRef}
            className="drag-joystick-base"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            <div
              className="drag-joystick-handle"
              style={{ transform: `translate(${stickPos.x}px, ${stickPos.y}px)` }}
            >
              <PawMark src={PAW_SRC} className="drag-joystick-paw" fallbackColor="#E8859D" />
            </div>
          </div>
        </div>

        <div className="drag-instruction">
          <span className="ko">드래그하여 원하는 위치로 이동하세요</span>
          <span className="en">Drag and move to the location you want</span>
        </div>

        <div className="drag-score">
          <ScoreBox value={score.total} />
        </div>
      </div>
      <BrandFooter />
    </div>
  );
}
